import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  TOKENS,
  DAY_MS,
  SEVEN_DAYS_MS,
  MAX_PROMPTS_PER_SESSION,
  RECEIVER_CHARGE_AMOUNT,
  STATUS,
  STATUS_LABELS,
  CURRENT_USER,
  MEMBERS,
  AI_FOLLOWUPS,
  SUMMARY_SEED,
} from "../Constants/MeetingGenieConst";
import "../Styles/MeetingGenieStyle.css";

function buildInitialBilling(createdAt) {
  return {
    freeUntil: createdAt + SEVEN_DAYS_MS,
    receiverChargeApplied: false,
    summaryDownloadFree: true,
    billingStatus: "free",
  };
}

function formatDateLong(ts) {
  return new Date(ts).toLocaleDateString("en-IN", { year: "numeric", month: "long", day: "numeric" });
}
function formatDateKey(ts) {
  const d = new Date(ts);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}
function formatTime(ts) {
  return new Date(ts).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" });
}
function initials(name) {
  return name
    .split(" ")
    .map((p) => p[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();
}
function formatCountdown(ms) {
  if (ms <= 0) return "00:00:00";
  const totalSeconds = Math.floor(ms / 1000);
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  const s = totalSeconds % 60;
  return [h, m, s].map((n) => String(n).padStart(2, "0")).join(":");
}
function formatDaysHours(ms) {
  if (ms <= 0) return "0 days 0 hours";
  const totalHours = Math.floor(ms / (60 * 60 * 1000));
  const days = Math.floor(totalHours / 24);
  const hours = totalHours % 24;
  return `${days} day${days === 1 ? "" : "s"} ${hours} hour${hours === 1 ? "" : "s"}`;
}
function buildSummaryText(dayMessages) {
  const senderLines = dayMessages.filter((m) => m.role === "sender").map((m) => m.message.trim());
  if (senderLines.length === 0) return SUMMARY_SEED;
  const main = senderLines[0];
  const rest = senderLines.slice(1);
  let text = `The sender's request centers on: ${main}`;
  if (rest.length > 0) {
    text += ` Additional detail shared during the conversation: ${rest.join(" ")}`;
  }
  return text;
}

function Avatar({ name, size = 56 }) {
  const sizeClass = size <= 40 ? "Gennie-avatar--sm" : size <= 56 ? "Gennie-avatar--md" : "Gennie-avatar--lg";
  return (
    <div className={`Gennie-avatar ${sizeClass}`}>
      {initials(name)}
    </div>
  );
}

function StatusBadge({ status }) {
  return <span className={`Gennie-badge Gennie-badge--${status}`}>{STATUS_LABELS[status] || status}</span>;
}

function PrimaryButton({ children, ...rest }) {
  return (
    <button className="Gennie-btn Gennie-btn--primary" {...rest}>
      {children}
    </button>
  );
}
function SecondaryButton({ children, ...rest }) {
  return (
    <button className="Gennie-btn Gennie-btn--secondary" {...rest}>
      {children}
    </button>
  );
}

function EmptyState({ title, body }) {
  return (
    <div className="Gennie-empty">
      <div className="Gennie-empty__title">{title}</div>
      <div className="Gennie-empty__body">{body}</div>
    </div>
  );
}

function MemberCard({ member, onMessage }) {
  return (
    <div className="Gennie-card Gennie-member-card">
      <div className="Gennie-member-card__top">
        <Avatar name={member.name} />
        <div className="Gennie-member-card__id">
          <div className="Gennie-member-card__name">{member.name}</div>
          <div className="Gennie-member-card__role">{member.designation}</div>
          <div className="Gennie-member-card__company">{member.company}</div>
          <div className="Gennie-member-card__location">{member.location}</div>
        </div>
      </div>
      <p className="Gennie-member-card__desc">{member.description}</p>
      <button className="Gennie-text-action" onClick={() => onMessage(member)}>
        Message
      </button>
    </div>
  );
}

function FindMembers({ onSelectMember }) {
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(false);

  function handleSearch(value) {
    setQuery(value);
    setLoading(true);
    window.clearTimeout(handleSearch._t);
    handleSearch._t = window.setTimeout(() => setLoading(false), 350);
  }

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return MEMBERS;
    return MEMBERS.filter(
      (m) =>
        m.name.toLowerCase().includes(q) ||
        m.company.toLowerCase().includes(q) ||
        m.designation.toLowerCase().includes(q)
    );
  }, [query]);

  return (
    <section className="Gennie-section">
      <h2 className="Gennie-heading-section">Find Members</h2>
      <p className="Gennie-subheading">Search by name, company, role</p>

      <input
        className="Gennie-input Gennie-input--search"
        type="text"
        placeholder="Search by name, company, or role"
        value={query}
        onChange={(e) => handleSearch(e.target.value)}
        aria-label="Search members"
      />

      {loading ? (
        <div className="Gennie-loading-grid">
          {[1, 2, 3].map((i) => (
            <div key={i} className="Gennie-card Gennie-skeleton" />
          ))}
        </div>
      ) : results.length === 0 ? (
        <EmptyState title="No Members Found" body="Try a different name, company, or role." />
      ) : (
        <div className="Gennie-member-grid">
          {results.map((m) => (
            <MemberCard key={m.id} member={m} onMessage={onSelectMember} />
          ))}
        </div>
      )}
    </section>
  );
}

function RequestStart({ member, onCancel, onStart }) {
  const [text, setText] = useState("");

  return (
    <section className="Gennie-section">
      <div className="Gennie-card Gennie-request-start">
        <h2 className="Gennie-heading-section">Requesting</h2>
        <div className="Gennie-request-start__member">
          <Avatar name={member.name} size={64} />
          <div>
            <div className="Gennie-member-card__name">{member.name}</div>
            <div className="Gennie-member-card__role">{member.designation}</div>
            <div className="Gennie-member-card__company">{member.company}</div>
          </div>
        </div>

        <label className="Gennie-label" htmlFor="Gennie-request-text">
          Tell Meeting Genie what you need help with
        </label>
        <textarea
          id="Gennie-request-text"
          className="Gennie-input Gennie-textarea"
          rows={5}
          placeholder="Tell Meeting Genie what you need help with..."
          value={text}
          onChange={(e) => setText(e.target.value)}
        />

        <div className="Gennie-actions">
          <SecondaryButton onClick={onCancel}>Cancel</SecondaryButton>
          <PrimaryButton disabled={!text.trim()} onClick={() => onStart(text.trim())}>
            Start Request
          </PrimaryButton>
        </div>
      </div>
    </section>
  );
}

function AISessionStatus({ request, mockNow }) {
  if (!request.aiActive) {
    return (
      <div className="Gennie-card Gennie-session-box Gennie-session-box--stopped">
        <div className="Gennie-session-box__title">AI Assistance Stopped</div>
        <div className="Gennie-session-box__body">This request no longer supports AI conversation.</div>
      </div>
    );
  }

  const promptsUsed = request.promptCount;
  const sessionComplete = promptsUsed >= MAX_PROMPTS_PER_SESSION;
  const nextAvailableAt = request.cycleStart + DAY_MS;
  const remaining = nextAvailableAt - mockNow;

  return (
    <div className="Gennie-card Gennie-session-box">
      <div className="Gennie-session-row">
        <div>
          <div className="Gennie-session-label">AI Session</div>
          <div className="Gennie-session-value">1 / 1</div>
        </div>
        <div>
          <div className="Gennie-session-label">Prompts This Session</div>
          <div className="Gennie-session-value">
            {promptsUsed} / {MAX_PROMPTS_PER_SESSION}
          </div>
        </div>
      </div>

      {sessionComplete ? (
        <div className="Gennie-session-cooldown">
          <div className="Gennie-session-cooldown__title">
            {MAX_PROMPTS_PER_SESSION} / {MAX_PROMPTS_PER_SESSION} prompts used
          </div>
          <div className="Gennie-session-cooldown__body">AI session completed.</div>
          <div className="Gennie-session-cooldown__label">Next AI access available in</div>
          <div className="Gennie-session-cooldown__timer">{formatCountdown(remaining)}</div>
        </div>
      ) : (
        <div className="Gennie-session-active">AI Active</div>
      )}
    </div>
  );
}

function AIChat({ request, messages, onSend, mockNow }) {
  const [draft, setDraft] = useState("");
  const listRef = useRef(null);

  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [messages.length]);

  const promptsUsed = request.promptCount;
  const canSend = request.aiActive && promptsUsed < MAX_PROMPTS_PER_SESSION;

  function handleSubmit(e) {
    e.preventDefault();
    if (!draft.trim() || !canSend) return;
    onSend(draft.trim());
    setDraft("");
  }

  return (
    <div className="Gennie-card Gennie-chat">
      <div className="Gennie-chat__header">
        <div className="Gennie-heading-section Gennie-chat__header-title">
          Meeting Genie
        </div>
        <div className="Gennie-subheading Gennie-chat__header-subtitle">
          IHN Agent
        </div>
      </div>

      <div className="Gennie-chat__list" ref={listRef}>
        {messages.length === 0 ? (
          <EmptyState title="No Conversation Yet" body="Your Meeting Genie conversation will appear here." />
        ) : (
          messages.map((m) => (
            <div key={m.id} className={`Gennie-msg Gennie-msg--${m.role}`}>
              <div className="Gennie-msg__role">{m.role === "ai" ? "IHN Agent" : "You"}</div>
              <div className="Gennie-msg__bubble">{m.message}</div>
              <div className="Gennie-msg__time">{formatTime(m.timestamp)}</div>
            </div>
          ))
        )}
      </div>

      {request.aiActive ? (
        <form className="Gennie-chat__composer" onSubmit={handleSubmit}>
          <input
            className="Gennie-input"
            type="text"
            placeholder={canSend ? "Type your message to the IHN Agent..." : "AI session unavailable right now"}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            disabled={!canSend}
            aria-label="Message the IHN Agent"
          />
          <PrimaryButton type="submit" disabled={!canSend || !draft.trim()}>
            Send
          </PrimaryButton>
        </form>
      ) : (
        <div className="Gennie-chat__closed">This conversation is closed. Existing messages remain available above.</div>
      )}
    </div>
  );
}

function SummaryPanel({ summaries, onDownload, showRequestMeta, request }) {
  const latest = summaries[summaries.length - 1];
  const history = summaries.slice(0, -1).reverse();

  return (
    <div className="Gennie-card Gennie-summary">
      <h3 className="Gennie-heading-sub">Latest Summary</h3>

      {!latest ? (
        <EmptyState title="No Daily Summaries" body="Summaries will appear as the AI conversation develops." />
      ) : (
        <>
          <div className="Gennie-summary__date">{formatDateLong(new Date(latest.date).getTime())}</div>
          <p className="Gennie-summary__text">{latest.summary}</p>
        </>
      )}

      {showRequestMeta && (
        <div className="Gennie-request-meta">
          <h4 className="Gennie-heading-sub">Request Details</h4>
          <div className="Gennie-request-meta__row">
            <span className="Gennie-request-meta__label">Purpose</span>
            <span>{request.requirementText}</span>
          </div>
          <div className="Gennie-request-meta__row">
            <span className="Gennie-request-meta__label">Status</span>
            <span>
              <StatusBadge status={request.status} />
            </span>
          </div>
        </div>
      )}

      {history.length > 0 && (
        <div className="Gennie-summary-history">
          <h4 className="Gennie-heading-sub">Summary History</h4>
          {history.map((s) => (
            <div key={s.date} className="Gennie-summary-history__item">
              <div className="Gennie-summary-history__date">{formatDateLong(new Date(s.date).getTime())}</div>
              <p className="Gennie-summary-history__text">{s.summary}</p>
            </div>
          ))}
        </div>
      )}

      <SecondaryButton onClick={onDownload} disabled={!latest}>
        Download Summary
      </SecondaryButton>
    </div>
  );
}

function BillingPanel({ billing, request, mockNow, role }) {
  const remainingFree = billing.freeUntil - mockNow;

  return (
    <div className="Gennie-card Gennie-billing">
      <h3 className="Gennie-heading-sub">Billing</h3>
      {billing.billingStatus === "free" ? (
        <>
          <div className="Gennie-billing__status Gennie-billing__status--free">Free Period Active</div>
          <p className="Gennie-billing__note">
            {role === "sender"
              ? "AI conversation, AI prompts, AI summaries and summary downloads are free during the first 7 days."
              : "Summary viewing and summary downloads are free. No AI chat is available to the receiver."}
          </p>
          {request.status === STATUS.PENDING && (
            <p className="Gennie-billing__note">Decision deadline in: {formatDaysHours(remainingFree)}</p>
          )}
        </>
      ) : (
        <>
          <div className="Gennie-billing__status Gennie-billing__status--charged">Decision Deadline Passed</div>
          <p className="Gennie-billing__note">
            The receiver did not respond within the 7-day decision period.
          </p>
          {role === "receiver" && (
            <p className="Gennie-billing__amount">Applicable receiver charge: {RECEIVER_CHARGE_AMOUNT}</p>
          )}
          <p className="Gennie-billing__note">Summary downloads remain free for both users.</p>
        </>
      )}
    </div>
  );
}

function ReceiverRequestBox({ member, request, summaries, billing, mockNow, onAccept, onReject, onDownload }) {
  return (
    <div className="Gennie-receiver">
      <div className="Gennie-card Gennie-receiver__top">
        <h2 className="Gennie-heading-section">New Request</h2>
        <div className="Gennie-request-meta__row">
          <span className="Gennie-request-meta__label">From</span>
          <span>{CURRENT_USER.name}</span>
        </div>
        <div className="Gennie-request-meta__row">
          <span className="Gennie-request-meta__label">Status</span>
          <span>
            <StatusBadge status={request.status} />
          </span>
        </div>

        {request.status === STATUS.PENDING && (
          <div className="Gennie-actions Gennie-actions--receiver">
            <PrimaryButton onClick={onAccept}>Accept</PrimaryButton>
            <SecondaryButton onClick={onReject}>Reject / Deny</SecondaryButton>
          </div>
        )}

        {request.status === STATUS.ACCEPTED && (
          <div className="Gennie-notice Gennie-notice--success">
            <div className="Gennie-notice__title">Request Accepted</div>
            <div>Meeting Genie AI assistance has ended. You can now communicate directly with the member.</div>
          </div>
        )}
        {request.status === STATUS.REJECTED && (
          <div className="Gennie-notice Gennie-notice--muted">
            <div className="Gennie-notice__title">Request Rejected</div>
            <div>The Meeting Genie AI conversation has ended. Existing summaries remain available.</div>
          </div>
        )}
        {request.status === STATUS.EXPIRED && (
          <div className="Gennie-notice Gennie-notice--warn">
            <div className="Gennie-notice__title">Request Expired</div>
            <div>The receiver did not make a decision within 7 days. AI assistance has ended. The applicable receiver-side charge has been applied.</div>
          </div>
        )}
      </div>

      <SummaryPanel summaries={summaries} onDownload={onDownload} showRequestMeta request={request} />
      <BillingPanel billing={billing} request={request} mockNow={mockNow} role="receiver" />
    </div>
  );
}

function SenderView({ request, member, messages, summaries, billing, mockNow, onSend, onDownload, onResend }) {
  return (
    <div className="Gennie-sender-layout">
      <div className="Gennie-sender-layout__left">
        <AISessionStatus request={request} mockNow={mockNow} />
        <AIChat request={request} messages={messages} onSend={onSend} mockNow={mockNow} />
      </div>
      <div className="Gennie-sender-layout__right">
        {request.status === STATUS.ACCEPTED && (
          <div className="Gennie-notice Gennie-notice--success">
            <div className="Gennie-notice__title">Request Accepted</div>
            <div>Meeting Genie AI assistance has ended. You can now communicate directly with the member.</div>
          </div>
        )}
        {request.status === STATUS.REJECTED && (
          <div className="Gennie-notice Gennie-notice--muted">
            <div className="Gennie-notice__title">Request Rejected</div>
            <div>The Meeting Genie AI conversation has ended. Your existing summaries remain available.</div>
          </div>
        )}
        {request.status === STATUS.EXPIRED && (
          <div className="Gennie-notice Gennie-notice--warn">
            <div className="Gennie-notice__title">Request Expired</div>
            <div>The receiver did not respond within the required period. Please send the request again.</div>
            <div className="Gennie-actions">
              <PrimaryButton onClick={onResend}>Send Request Again</PrimaryButton>
            </div>
          </div>
        )}
        {request.status === STATUS.PENDING && (
          <div className="Gennie-notice Gennie-notice--info">
            <div className="Gennie-notice__title">Decision Required</div>
            <div>Waiting for {member.name} to accept or reject this request.</div>
          </div>
        )}

        <SummaryPanel summaries={summaries} onDownload={onDownload} showRequestMeta={false} request={request} />
        <BillingPanel billing={billing} request={request} mockNow={mockNow} role="sender" />
      </div>
    </div>
  );
}

function DevControls({ hasRequest, request, onAdvance, onReset, onAccept, onReject, onSendMockPrompt, mockNow }) {
  return (
    <div className="Gennie-card Gennie-dev">
      <h3 className="Gennie-heading-sub">Development Demo Controls</h3>
      <p className="Gennie-dev__note">
        For frontend testing only. Mock current time: {new Date(mockNow).toLocaleString("en-IN")}
      </p>
      <div className="Gennie-dev__grid">
        <SecondaryButton onClick={() => onAdvance(DAY_MS)} disabled={!hasRequest}>
          Move 24 Hours Forward
        </SecondaryButton>
        <SecondaryButton onClick={() => onAdvance(SEVEN_DAYS_MS)} disabled={!hasRequest}>
          Move 7 Days Forward
        </SecondaryButton>
        <SecondaryButton onClick={onSendMockPrompt} disabled={!hasRequest || !request?.aiActive}>
          Send Mock Prompt
        </SecondaryButton>
        <SecondaryButton onClick={onAccept} disabled={!hasRequest || request?.status !== STATUS.PENDING}>
          Accept Request
        </SecondaryButton>
        <SecondaryButton onClick={onReject} disabled={!hasRequest || request?.status !== STATUS.PENDING}>
          Reject Request
        </SecondaryButton>
        <SecondaryButton onClick={onReset} disabled={!hasRequest}>
          Reset Request
        </SecondaryButton>
      </div>
    </div>
  );
}

export default function MeetingGenie() {
  const [mockNow, setMockNow] = useState(new Date("2026-08-17T10:00:00").getTime());
  const [selectedMember, setSelectedMember] = useState(null);
  const [request, setRequest] = useState(null);
  const [messages, setMessages] = useState([]);
  const [summaries, setSummaries] = useState([]);
  const [billing, setBilling] = useState(null);
  const [role, setRole] = useState("sender");
  const [downloadNotice, setDownloadNotice] = useState("");

  useEffect(() => {
    const id = setInterval(() => setMockNow((t) => t + 1000), 1000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    if (!request || !request.aiActive) return;
    const elapsed = mockNow - request.cycleStart;
    if (elapsed >= DAY_MS) {
      const cyclesPassed = Math.floor(elapsed / DAY_MS);
      setRequest((r) => (r ? { ...r, cycleStart: r.cycleStart + cyclesPassed * DAY_MS, promptCount: 0 } : r));
    }
  }, [mockNow, request]);

  useEffect(() => {
    if (!request || request.status !== STATUS.PENDING) return;
    if (mockNow >= request.decisionDeadline) {
      setRequest((r) => (r ? { ...r, status: STATUS.EXPIRED, aiActive: false } : r));
      setBilling((b) => (b ? { ...b, billingStatus: "receiver_charge_applied", receiverChargeApplied: true } : b));
    }
  }, [mockNow, request]);

  useEffect(() => {
    if (!downloadNotice) return;
    const id = setTimeout(() => setDownloadNotice(""), 2500);
    return () => clearTimeout(id);
  }, [downloadNotice]);

  function handleSelectMember(member) {
    setSelectedMember(member);
  }

  function handleCancelRequestStart() {
    setSelectedMember(null);
  }

  function handleStartRequest(text) {
    const createdAt = mockNow;
    const newRequest = {
      id: Date.now(),
      senderId: CURRENT_USER.id,
      receiverId: selectedMember.id,
      createdAt,
      status: STATUS.PENDING,
      aiActive: true,
      promptCount: 0,
      cycleStart: createdAt,
      decisionDeadline: createdAt + SEVEN_DAYS_MS,
      requirementText: text,
    };
    setRequest(newRequest);
    setBilling(buildInitialBilling(createdAt));
    setMessages([]);
    setSummaries([]);
    setRole("sender");
    sendPrompt(newRequest, [], text);
  }

  function sendPrompt(currentRequest, currentMessages, text) {
    const now = mockNow;
    const promptIndex = currentRequest.promptCount;
    const senderMsg = { id: `${now}-s`, role: "sender", message: text, timestamp: now };
    const aiText = AI_FOLLOWUPS[Math.min(promptIndex, AI_FOLLOWUPS.length - 1)];
    const aiMsg = { id: `${now}-a`, role: "ai", message: aiText, timestamp: now + 1000 };
    const nextMessages = [...currentMessages, senderMsg, aiMsg];

    setMessages(nextMessages);
    setRequest((r) => (r ? { ...r, promptCount: r.promptCount + 1 } : r));

    const dateKey = formatDateKey(now);
    const dayMessages = nextMessages.filter((m) => formatDateKey(m.timestamp) === dateKey);
    const summaryText = buildSummaryText(dayMessages);
    setSummaries((prev) => {
      const idx = prev.findIndex((s) => s.date === dateKey);
      const entry = { date: dateKey, summary: summaryText, finalized: true, downloadable: true };
      if (idx === -1) return [...prev, entry];
      const copy = [...prev];
      copy[idx] = entry;
      return copy;
    });
  }

  function handleSend(text) {
    if (!request || !request.aiActive) return;
    if (request.promptCount >= MAX_PROMPTS_PER_SESSION) return;
    sendPrompt(request, messages, text);
  }

  function handleSendMockPrompt() {
    if (!request || !request.aiActive) return;
    if (request.promptCount >= MAX_PROMPTS_PER_SESSION) return;
    const mockTexts = [
      "We expect around 50 people and need September availability.",
      "The budget range is flexible depending on the package offered.",
      "We would prefer a venue close to the airport if possible.",
      "This may become a recurring monthly booking.",
      "Please also share information about catering options.",
    ];
    sendPrompt(request, messages, mockTexts[request.promptCount % mockTexts.length]);
  }

  function handleAccept() {
    setRequest((r) => (r ? { ...r, status: STATUS.ACCEPTED, aiActive: false } : r));
  }
  function handleReject() {
    setRequest((r) => (r ? { ...r, status: STATUS.REJECTED, aiActive: false } : r));
  }
  function handleDownload() {
    setDownloadNotice("Summary downloaded (mock).");
  }
  function handleResend() {
    setRequest(null);
    setMessages([]);
    setSummaries([]);
    setBilling(null);
    setSelectedMember(null);
    setRole("sender");
  }
  function handleAdvance(ms) {
    setMockNow((t) => t + ms);
  }
  function handleReset() {
    if (!selectedMember) return;
    const createdAt = mockNow;
    const newRequest = {
      id: Date.now(),
      senderId: CURRENT_USER.id,
      receiverId: selectedMember.id,
      createdAt,
      status: STATUS.PENDING,
      aiActive: true,
      promptCount: 0,
      cycleStart: createdAt,
      decisionDeadline: createdAt + SEVEN_DAYS_MS,
      requirementText: request ? request.requirementText : "New request",
    };
    setRequest(newRequest);
    setBilling(buildInitialBilling(createdAt));
    setMessages([]);
    setSummaries([]);
  }

  const receiverMember = request ? MEMBERS.find((m) => m.id === request.receiverId) : null;

  return (
    <div className="Gennie-page">
      <header className="Gennie-header">
        <div className="Gennie-page-container">
          <h1 className="Gennie-heading-main">Meeting Genie</h1>
          <p className="Gennie-subheading Gennie-subheading--lead">
            AI-assisted member requests, organized into clear summaries for the people you work with.
          </p>
        </div>
      </header>

      <main className="Gennie-page-container Gennie-main">
        {!request && !selectedMember && <FindMembers onSelectMember={handleSelectMember} />}

        {!request && selectedMember && (
          <RequestStart member={selectedMember} onCancel={handleCancelRequestStart} onStart={handleStartRequest} />
        )}

        {request && receiverMember && (
          <section className="Gennie-section">
            <div className="Gennie-role-toggle" role="tablist" aria-label="View as">
              <button
                role="tab"
                aria-selected={role === "sender"}
                className={`Gennie-role-toggle__btn ${role === "sender" ? "is-active" : ""}`}
                onClick={() => setRole("sender")}
              >
                Sender View
              </button>
              <button
                role="tab"
                aria-selected={role === "receiver"}
                className={`Gennie-role-toggle__btn ${role === "receiver" ? "is-active" : ""}`}
                onClick={() => setRole("receiver")}
              >
                Receiver View
              </button>
            </div>

            {downloadNotice && <div className="Gennie-toast">{downloadNotice}</div>}

            {role === "sender" ? (
              <SenderView
                request={request}
                member={receiverMember}
                messages={messages}
                summaries={summaries}
                billing={billing}
                mockNow={mockNow}
                onSend={handleSend}
                onDownload={handleDownload}
                onResend={handleResend}
              />
            ) : (
              <ReceiverRequestBox
                member={receiverMember}
                request={request}
                summaries={summaries}
                billing={billing}
                mockNow={mockNow}
                onAccept={handleAccept}
                onReject={handleReject}
                onDownload={handleDownload}
              />
            )}
          </section>
        )}

        <DevControls
          hasRequest={!!request}
          request={request}
          onAdvance={handleAdvance}
          onReset={handleReset}
          onAccept={handleAccept}
          onReject={handleReject}
          onSendMockPrompt={handleSendMockPrompt}
          mockNow={mockNow}
        />
      </main>
    </div>
  );
}
